import {
  FC,
  HTMLAttributes,
  useEffect,
  useRef
} from 'react';
import { useSubject } from '../hooks';
import { BehaviorSubject, LanguageCode } from '../types';

export type AudioControllerProps = {
  /** When true, start playback once the source is ready. */
  autoPlay?: boolean;
  /**
   * Bumps to re-trigger autoplay for the same `url` (e.g. regenerate).
   * Remounts the element when `autoPlay` is true.
   */
  autoPlayKey?: number;
  /** When true, playback controls are inert. */
  disabled?: boolean;
  language?: LanguageCode;
  url: (
    BehaviorSubject<string | null>
    | string
    | null
  );
} & Pick<HTMLAttributes<HTMLAudioElement>, 'onError'>;

export const AudioController: FC<AudioControllerProps> = ({
  autoPlay = false,
  autoPlayKey = 0,
  disabled = false,
  language,
  url: urlProp,
  ...audioElementProps
}) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const urlSubject = (urlProp instanceof BehaviorSubject) ? (
    urlProp
  ) : new BehaviorSubject<string | null>(urlProp);
  const url = useSubject<string | null>(urlSubject, urlSubject.value);

  useEffect(() => {
    if (disabled) {
      audioRef.current?.pause();
    }
  }, [disabled]);

  return (!url) ? null : (
    <audio
      aria-disabled={disabled}
      autoPlay={autoPlay && !disabled}
      controls
      controlsList="nodownload"
      // Remount when autoPlayKey bumps so native autoplay runs again for the same src.
      key={(autoPlay) ? `${url}:${autoPlayKey}` : url}
      lang={language}
      ref={audioRef}
      src={url}
      style={{
        opacity: (disabled) ? 0.5 : undefined,
        pointerEvents: (disabled) ? 'none' : undefined,
        width: '100%'
      }}
      tabIndex={(disabled) ? -1 : undefined}
      {...audioElementProps}
    />
  );
};
